import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import Form from "../../../components/Form/Form";
import FormTitle from "../../../components/FormTitle/FormTitle";
import Input from "../../../components/Input/Input";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import { useListManager } from "../../../contexts/list.manager";
import InvitationService from "../../../services/InvitationService";
import { LuUserPlus } from "react-icons/lu";
import { NotificationManager } from "react-notifications";
import "./FormInvitation.css";
import { SelectAccessGroup } from "../../../components/SelectAccessGroup/SelectAccessGroup";
import { useAuth } from "../../../contexts/auth";
import { InvitationType, InvitationTypeText } from "../../../enums/invitationTypes";
import { InvitationTypeIcons } from "../../../utils/invitationTypeIcons";
import { date } from "../../../utils/date";
import { GoArrowSwitch } from "react-icons/go";
import { InvitationAccessModulesDDL } from "../../../components/InvitationAccessModulesDDL/InvitationAccessModulesDDL";
import { Map } from "../../../components/Map/Map";

function FormInvitation(props) {
	const [name, setName] = useState(props.invitation?.name || "");
	const [phoneNumber, setPhoneNumber] = useState(props.invitation?.phone || "");
	const [plate, setPlate] = useState(props.invitation?.plate || "");
	const [isUsingGroup, setIsUsingGroup] = useState(props.invitation?.useGroup);
	const [allowGuestToFillPlate, setAllowGuestToFillPlate] = useState(props.invitation?.allowGuestToFillPlate ?? false);
	const [group, setGroup] = useState(props.invitation?.group || "");
	const [startDate, setStartDate] = useState(props.invitation?.startDate || "");
	const [endDate, setEndDate] = useState(props.invitation?.endDate || "");
	const [type, setType] = useState(props.invitation?.type || InvitationType.FACIAL);
	const [enforceGeolocationCheck, setEnforceGeolocationCheck] = useState(props.invitation?.enforceGeolocationCheck ?? false);
	const [geolocationRadius, setGeolocationRadius] = useState(props.invitation?.geolocationRadius ?? null);
	const [customCenterLatitude, setCustomCenterLatitude] = useState(props.invitation?.customCenterLatitude ?? null);
	const [customCenterLongitude, setCustomCenterLongitude] = useState(props.invitation?.customCenterLongitude ?? null);
	const [credits, setCredits] = useState(props.invitation?.credits ?? 1);
	const [accessSelected, setAccessSelected] = useState(
		props.invitation?.accessModules
	);
	const [mapCenter, setMapCenter] = useState(
		props.invitation?.customCenterLatitude && props.invitation?.customCenterLongitude
			? { latitude: props.invitation.customCenterLatitude, longitude: props.invitation.customCenterLongitude }
			: null
	);

	const { user } = useAuth();
	const { closeForm } = useListManager();
	const navigate = useNavigate();

	useEffect(() => {
		setName(props.invitation?.name || "");
		setPhoneNumber(props.invitation?.phone || "");
		setPlate(props.invitation?.plate || "");
		setGroup(props.invitation?.group || undefined);
		setIsUsingGroup(props.invitation?.useGroup);
		setAllowGuestToFillPlate(props.invitation?.allowGuestToFillPlate ?? false);
		setStartDate(props.invitation ? date("Y-m-dTH:i", props.invitation.startDate) : "");
		setEndDate(props.invitation ? date("Y-m-dTH:i", props.invitation.endDate) : "");
		setType(props.invitation?.type || InvitationType.FACIAL);
		setEnforceGeolocationCheck(props.invitation?.enforceGeolocationCheck ?? false);
		setGeolocationRadius(props.invitation?.geolocationRadius ?? null);
		setCustomCenterLatitude(props.invitation?.customCenterLatitude ?? null);
		setCustomCenterLongitude(props.invitation?.customCenterLongitude ?? null);
		setAccessSelected(props.invitation?.invitationAccessModules || []);
		setCredits(props.invitation?.credits ?? 1);
		setMapCenter(props.invitation?.customCenterLatitude && props.invitation?.customCenterLongitude
			? { latitude: props.invitation.customCenterLatitude, longitude: props.invitation.customCenterLongitude }
			: null);
	}, [props.invitation]);

	// Fetch browser geolocation when enforceGeolocationCheck is enabled and no center set
	useEffect(() => {
		if (
			type === InvitationType.VIRTUAL_KEY &&
			enforceGeolocationCheck &&
			!mapCenter &&
			navigator.geolocation
		) {
			navigator.geolocation.getCurrentPosition(
				(pos) => {
					const { latitude, longitude } = pos.coords;
					setMapCenter({ latitude, longitude });
					if (!customCenterLatitude) setCustomCenterLatitude(latitude);
					if (!customCenterLongitude) setCustomCenterLongitude(longitude);
				},
				() => { },
				{ enableHighAccuracy: true, timeout: 10000 }
			);
		}
	}, [type, enforceGeolocationCheck]);

	const buildPayload = () => {
		return {
			name,
			phone: phoneNumber,
			plate,
			type,
			useGroup: isUsingGroup,
			groupId: isUsingGroup ? Number(group) : undefined,
			startDate,
			endDate,
			uuid: props.invitation?.uuid || uuidv4(),
			enforceGeolocationCheck,
			allowGuestToFillPlate,
			id_organization: user.userData.selectedOrganization?.id,
			geolocationRadius,
			customCenterLatitude,
			customCenterLongitude,
			credits: Number(credits),
			invitationAccessModules: accessSelected?.map(accessSelected => ({
				id: accessSelected.relationId ?? null,
				moduleId: accessSelected.originalId,
				accessIndex: accessSelected.subIndex,
			})) || [],
		};
	};

	async function handleSubmit(event) {
		if (event) event.preventDefault();

		if (!name) {
			NotificationManager.warning("Preencha o nome", "Atenção", 5000);
			return;
		}

		if (isUsingGroup && !group) {
			NotificationManager.warning("Selecione um grupo de acesso", "Atenção", 5000);
			return;
		}

		if (!isUsingGroup && accessSelected?.length === 0) {
			NotificationManager.warning("Selecione um acesso", "Atenção", 5000);
			return;
		}

		if (type === InvitationType.VIRTUAL_KEY && !credits) {
			NotificationManager.warning("Selecione a quantidade de créditos", "Atenção", 5000);
			return;
		}

		const payload = buildPayload();
		let response;

		if (props.type === "edicao") {
			response = await InvitationService.updateInvitation(props.invitation.id, payload);
		} else {
			response = await InvitationService.createInvitation(payload);
		}

		if (response.status === 200 || response.status === 201) {
			navigate("../consulta-invitations", { state: { refresh: true } });
			closeForm();
		}
	}

	const handleChangeDualList = (accessSelectedArray) => {
		setAccessSelected(accessSelectedArray);
	};

	async function handleDelete() {
		if (!props.invitation) return;
		const response = await InvitationService.deleteInvitation(props.invitation.id);
		if (response.status === 200 || response.status === 204) {
			navigate("../consulta-invitations", { state: { deletedId: props.invitation.id } });
			closeForm();
		}
	}

	const handleClickEditButton = () => {
		if (!props.invitation) return;
		navigate(`/invitations/${props.invitation.uuid}`, { state: props.invitation });
	};

	return (
		<div className={`form-invitation ${props.type === 'consulta' ? 'form-invitation-consulta' : ''}`}>
			<FormTitle
				type={props.type}
				icon={<LuUserPlus size={20} />}
				tituloConsulta={props.invitation?.name || null}
				tituloCadastro="Cadastro de Convite"
				backRoute={"/consulta-invitations"}
			/>
			<Form
				handleSubmit={handleSubmit}
				type={props.type}
				handleClickEdit={handleClickEditButton}
				handleDelete={handleDelete}
				navigateCancelUrl="../consulta-invitations"
				deleteButNotSynqed={props.invitation?.deletedAt ? true : false}
			>
				<p className="input-label">Tipo do convite</p>
				<div className="tag-type-selector">
					<div>
						<input
							id="vehicle"
							type="radio"
							name="invitation-type"
							disabled={props.type !== "cadastro"}
							value={InvitationType.VEHICLE}
							onChange={e => setType(e.target.value)}
							checked={type === InvitationType.VEHICLE}
						/>
						<label htmlFor="vehicle" className="flex-row! items-center! w-fit! h-fit!">
							{React.createElement(InvitationTypeIcons.VEHICLE, { size: 27 })}
							{InvitationTypeText[InvitationType.VEHICLE]}
						</label>
					</div>
					<div>
						<input
							id="facial"
							type="radio"
							name="invitation-type"
							disabled={props.type !== "cadastro"}
							value={InvitationType.FACIAL}
							onChange={e => setType(e.target.value)}
							checked={type === InvitationType.FACIAL}
						/>
						<label htmlFor="facial" className="flex-row! items-center! w-fit! h-fit!">
							{React.createElement(InvitationTypeIcons.FACIAL, { size: 27 })}
							{InvitationTypeText[InvitationType.FACIAL]}
						</label>
					</div>
					<div>
						<input
							id="virtual-key"
							type="radio"
							name="invitation-type"
							disabled={props.type !== "cadastro"}
							value={InvitationType.VIRTUAL_KEY}
							onChange={e => setType(e.target.value)}
							checked={type === InvitationType.VIRTUAL_KEY}
						/>
						<label htmlFor="virtual-key" className="flex-row! items-center! w-fit! h-fit!">
							{React.createElement(InvitationTypeIcons.VIRTUAL_KEY, { size: 27 })}
							{InvitationTypeText[InvitationType.VIRTUAL_KEY]}
						</label>
					</div>
					<div>
						<input
							id="qrcode"
							type="radio"
							name="invitation-type"
							disabled={props.type !== "cadastro"}
							value={InvitationType.QRCODE}
							onChange={e => setType(e.target.value)}
							checked={type === InvitationType.QRCODE}
						/>
						<label htmlFor="qrcode" className="flex-row! items-center! w-fit! h-fit!">
							{React.createElement(InvitationTypeIcons.QRCODE, { size: 27 })}
							{InvitationTypeText[InvitationType.QRCODE]}
						</label>
					</div>
				</div>
				<div className="grid gap-3 grid-cols-2 w-full mt-1!">
					<div className="form-inputs-name">
						<Input
							name="name"
							id="name"
							labelText="Nome *"
							value={name}
							onChange={({ target }) => setName(target.value)}
							disabled={props.type === "consulta"}
							required={true}
							tooltipText="Nome do convidado."
							tooltipVisible={true}
						/>
					</div>
					<div className="form-inputs-name">
						<Input
							name="phone_number"
							id="phone_number"
							labelText="Telefone"
							value={phoneNumber}
							onChange={({ target }) => setPhoneNumber(target.value)}
							disabled={props.type === "consulta"}
							placeholder="(00) 00000-0000"
							showPhoneMask={true}
							minLength="15"
							maxLength="15"
							tooltipText="Telefone do convidado."
							tooltipVisible={true}
						/>
					</div>
					<div className="form-inputs-name">
						<Input
							name="startDate"
							id="startDate"
							labelText="Início em *"
							type="datetime-local"
							value={startDate}
							onChange={({ target }) => setStartDate(target.value)}
							disabled={props.type === "consulta"}
							required={true}
							tooltipText=""
							tooltipVisible={true}
						/>
					</div>
					<div className="form-inputs-name">
						<Input
							name="endDate"
							id="endDate"
							labelText="Término em *"
							type="datetime-local"
							value={endDate}
							onChange={({ target }) => setEndDate(target.value)}
							disabled={props.type === "consulta"}
							required={true}
							tooltipText=""
							tooltipVisible={true}
						/>
					</div>
					{type === InvitationType.VEHICLE && (
						<>
							{!allowGuestToFillPlate && (
								<div className="form-inputs-name">
									<Input
										name="plate"
										id="plate"
										labelText="Placa"
										value={plate}
										onChange={({ target }) => setPlate(target.value)}
										disabled={props.type === "consulta"}
										tooltipText="Placa do veículo do convidado"
										tooltipVisible={true}
									/>
								</div>
							)}
							<div className="flex mb-2!">
								<ToggleButton
									label={`Permitir que o convidado informe a placa`}
									selected={allowGuestToFillPlate}
									onClick={() => setAllowGuestToFillPlate(!allowGuestToFillPlate)}
									disabled={props.type === "consulta"}
								/>
							</div>
						</>
					)}
					{type === InvitationType.VIRTUAL_KEY && (
						<div className="form-inputs-name">
							<Input
								name="credits"
								id="credits"
								labelText="Créditos *"
								type="number"
								value={credits}
								onChange={({ target }) => setCredits(target.value)}
								disabled={props.type === "consulta"}
								tooltipText="Créditos do convite"
								tooltipVisible={true}
								required={true}
							/>
						</div>
					)}
					<div className="col-span-2 mt-2!">
						<h3 className="muted-md bordered mt-0!">
							<GoArrowSwitch size={18} />
							Acessos liberados
						</h3>
						<div className="flex flex-col gap-3">
							<ToggleButton
								label={`Utilizar grupo de acessos`}
								selected={isUsingGroup}
								onClick={() => setIsUsingGroup(!isUsingGroup)}
								disabled={props.type === "consulta"}
							/>
							{isUsingGroup ? (
								<SelectAccessGroup
									formMode={props.type}
									group={group}
									onChange={(value) => setGroup(value)}
									required={false}
									onlyUnit={false}
								/>
							) : (
								<>
									{props.type !== "consulta" && (
										<div className="container-dual-list-access-groups w-full!" style={{ display: "flex" }}>
											<div className="form-cond-dual-list-container w-full">
												<InvitationAccessModulesDDL
													onChangeList={handleChangeDualList}
													invitation={props.invitation}
												/>
											</div>
										</div>
									)}
								</>
							)}
						</div>
					</div>
					{type === InvitationType.VIRTUAL_KEY && (
						<div className="col-span-2 mt-1! flex flex-col gap-3">
							<div className="flex">
								<ToggleButton
									label="Verificar geolocalização do convidado"
									className="pt-2!"
									defaultChecked={enforceGeolocationCheck}
									onChange={({ target }) => setEnforceGeolocationCheck(target.checked)}
									required={false}
									disabled={props.type === "consulta"}
									tooltipText={
										"Obriga o convidado a estar próximo da unidade para utilizar a chave virtual"
									}
									tooltipVisible={true}
								/>
							</div>
							{enforceGeolocationCheck && (
								<div className="form-inputs-name">
									<Input
										name="geolocationRadius"
										id="geolocationRadius"
										labelText="Raio (metros)"
										value={geolocationRadius}
										onChange={({ target }) => setGeolocationRadius(target.value)}
										disabled={props.type === "consulta"}
										type="number"
										min="0"
										step="10"
										tooltipText="Raio de geolocalização em metros"
										tooltipVisible={true}
									/>
								</div>
								// <div className="geolocation-map-wrapper">
								// 	{mapCenter ? (
								// 		<Map
								// 			geolocation={mapCenter}
								// 			radius={geolocationRadius}
								// 			draggable={false}
								// 			showRadiusControl={props.type !== "consulta"}
								// 			showCoordinateControls={props.type !== "consulta"}
								// 			readOnly={props.type === "consulta"}
								// 			onRadiusChange={(value) => setGeolocationRadius(value)}
								// 			onCenterChange={(center) => {
								// 				setMapCenter(center);
								// 				setCustomCenterLatitude(center.latitude);
								// 				setCustomCenterLongitude(center.longitude);
								// 			}}
								// 		/>
								// 	) : (
								// 		<div className="geolocation-map-loading">
								// 			<span>Obtendo localização...</span>
								// 		</div>
								// 	)}
								// </div>
							)}
						</div>
					)}
				</div>
			</Form >
		</div >
	);
}

export default FormInvitation;
